import React from "react";

/**
 * Minimal markdown renderer for chat messages.
 *
 * The assistant writes markdown because the knowledge base is written in it —
 * `**Send Verification Code**`, numbered steps, bullet lists — and rendering
 * that raw showed literal asterisks to the user.
 *
 * **Why not a markdown library, and why no `dangerouslySetInnerHTML`:**
 * message text is untrusted. It carries whatever the model produced from a
 * user's own words, and the same component renders admin replies. Building an
 * HTML string from that and injecting it would be an XSS hole in a widget
 * that sits on every authenticated page of both portals. This produces React
 * elements directly, so nothing is ever parsed as HTML — a message containing
 * `<script>` renders as the literal text `<script>`.
 *
 * Scope is deliberately small: bold, italic, inline code, bullet and numbered
 * lists, and markdown links whose target is an in-app path. No images, no raw
 * HTML. Anything unrecognised falls through as plain text, which is the safe
 * failure.
 *
 * **Links are restricted to same-origin paths** (`[Invitations](/invitations)`).
 * An absolute URL is rendered as plain text instead, so a message that reaches
 * this component can never become a clickable link to somewhere else — which
 * matters because message text is model output shaped by user input, and the
 * same component renders admin replies.
 */

/**
 * Split one line into text and inline-formatted spans.
 *
 * Returns a single keyed fragment rather than a bare array: an array mixing
 * plain strings with elements has unkeyed members, which React 19 rejects
 * with "Objects are not valid as a React child". Wrapping each piece in a
 * keyed Fragment keeps the whole thing a valid single child.
 */
function renderInline(text: string, keyPrefix: string): React.ReactNode {
  const nodes: React.ReactNode[] = [];
  // Order matters: `**bold**` must be tried before `*italic*`, or the italic
  // pattern claims the first two asterisks of a bold run.
  // Link first: its label can itself contain bold, and matching bold first
  // would split the link apart.
  const pattern =
    /(\[[^\]\n]+\]\([^)\s]+\)|\*\*[^*]+\*\*|\*[^*\n]+\*|`[^`\n]+`)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let i = 0;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    const token = match[0];
    const key = `${keyPrefix}-i${i++}`;

    if (token.startsWith("[")) {
      const m = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(token);
      const label = m?.[1] ?? token;
      const href = m?.[2] ?? "";
      // Only in-app paths become links. Anything else — http(s), javascript:,
      // protocol-relative // — renders as text.
      const safe = href.startsWith("/") && !href.startsWith("//");
      nodes.push(
        safe ? (
          <a key={key} href={href} className="lh-support__link">
            {label}
          </a>
        ) : (
          <React.Fragment key={key}>{label}</React.Fragment>
        ),
      );
    } else if (token.startsWith("**")) {
      nodes.push(<strong key={key}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith("`")) {
      nodes.push(
        <code key={key} className="lh-support__code">
          {token.slice(1, -1)}
        </code>,
      );
    } else {
      nodes.push(<em key={key}>{token.slice(1, -1)}</em>);
    }
    lastIndex = match.index + token.length;
  }

  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));

  return (
    <>
      {nodes.map((node, n) => (
        <React.Fragment key={`${keyPrefix}-n${n}`}>{node}</React.Fragment>
      ))}
    </>
  );
}

type Block =
  | { type: "p"; lines: string[] }
  | { type: "ul"; items: string[] }
  | { type: "ol"; items: string[] };

/**
 * Group lines into blocks. Consecutive list items become one list; everything
 * else accumulates into a paragraph until a blank line ends it.
 */
function toBlocks(body: string): Block[] {
  const blocks: Block[] = [];
  let current: Block | null = null;

  const flush = () => {
    if (current) blocks.push(current);
    current = null;
  };

  for (const raw of body.split("\n")) {
    const line = raw.trimEnd();
    const bullet = /^\s*[-*]\s+(.*)$/.exec(line);
    const numbered = /^\s*\d+[.)]\s+(.*)$/.exec(line);

    if (bullet) {
      if (current?.type !== "ul") {
        flush();
        current = { type: "ul", items: [] };
      }
      (current as { type: "ul"; items: string[] }).items.push(bullet[1]);
    } else if (numbered) {
      if (current?.type !== "ol") {
        flush();
        current = { type: "ol", items: [] };
      }
      (current as { type: "ol"; items: string[] }).items.push(numbered[1]);
    } else if (line.trim() === "") {
      flush();
    } else {
      if (current?.type !== "p") {
        flush();
        current = { type: "p", lines: [] };
      }
      (current as { type: "p"; lines: string[] }).lines.push(line);
    }
  }
  flush();
  return blocks;
}

export const Markdown: React.FC<{ body: string }> = ({ body }) => {
  const blocks = toBlocks(body);

  return (
    <>
      {blocks.map((block, bi) => {
        if (block.type === "ul") {
          return (
            <ul key={bi} className="lh-support__list">
              {block.items.map((item, ii) => (
                <li key={ii}>{renderInline(item, `${bi}-${ii}`)}</li>
              ))}
            </ul>
          );
        }
        if (block.type === "ol") {
          return (
            <ol key={bi} className="lh-support__list">
              {block.items.map((item, ii) => (
                <li key={ii}>{renderInline(item, `${bi}-${ii}`)}</li>
              ))}
            </ol>
          );
        }
        // Soft line breaks inside a paragraph are preserved — the assistant
        // uses them for short step lists that aren't marked up as one.
        return (
          <p key={bi} className="lh-support__para">
            {block.lines.map((line, li) => (
              <React.Fragment key={li}>
                {li > 0 && <br />}
                {renderInline(line, `${bi}-${li}`)}
              </React.Fragment>
            ))}
          </p>
        );
      })}
    </>
  );
};

export default Markdown;
